import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { ClickAwayListener, DialogActions, DialogContent, DialogTitle, Grow, MenuItem, Paper, Popper } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QuestionIcon16pxSvg from '../../../assets/images/icons/QuestionIcon16pxSvg';
import avatar_2 from '../../../assets/images/logo/avatar-default-logo.png';
import DefaultTooltip from '../../../components/DefaultTooltip';
import {
   ControlpanelErrorButton,
   ControlpanelPrimaryButton,
   ControlpanelSecondaryButton,
   ControlpanelSuccessButton,
   GrayIconButton,
} from '../../../components/StyledButton';
import { CustomAdvancedMenuList, CustomDialog } from '../../../components/StyledComponents';
import UploadIcon14pxSvg from './../../../assets/images/icons/UploadIcon14pxSvg';
import EnrollFromHistoryDialog from './EnrollFromHistoryDialog';

const BiometricDataDialog = ({ firstName, lastName, userGroup, onClose }) => {
   const { t } = useTranslation();

   const imageInputRef = useRef(null);

   const handleIconClick = () => {
      imageInputRef.current.click();
   };

   const handleImageChange = (event) => {
      const file = event.target.files[0];
      if (file) {
         const reader = new FileReader();
         reader.readAsDataURL(file);
      }
   };

   const [selectedImageIndex, setSelectedImageIndex] = useState(-1);

   const handleDivClick = (index) => {
      setSelectedImageIndex(index); // Update the selected state
   };

   const [openSelectType, setOpenSelectType] = useState(false);
   const [selectedType, setSelectedType] = useState(t('button.upload-face-image'));
   const anchorRefSelectType = useRef(null);

   const handleCloseSelectType = (event) => {
      if (anchorRefSelectType.current && anchorRefSelectType.current.contains(event.target)) {
         return;
      }
      setOpenSelectType(false);
   };

   const handleToggleSelectType = () => {
      setOpenSelectType(!openSelectType);
   };

   const handleSelectedItemClick = (type) => {
      setSelectedType(type);
      setOpenSelectType(false);
   };

   const [openEnrollFromHistory, setOpenEnrollFromHistory] = useState(false);
   const handleOpenEnrollFromHistory = () => {
      setOpenEnrollFromHistory(true);
   };

   const handleCloseEnrollFromHistory = () => {
      setOpenEnrollFromHistory(false);
   };

   return (
      <div>
         <CustomDialog transitionDuration={300} maxWidth={'xl'} open onClose={onClose} keepMounted>
            <DialogTitle>
               <div className='flex justify-between'>
                  <div className='flex items-center gap-4'>
                     <p className='text-xl font-semibold '>
                        {t('common.biometric-data')} - {firstName}&nbsp;{lastName}
                     </p>
                     <DefaultTooltip
                        readMore={true}
                        title={t('tool-tip.change-biometrics')}
                        content={
                           <div className='flex flex-col gap-3'>
                              <p>{t('tool-tip.in-the-biometric-data-dialogue')}</p>
                           </div>
                        }
                     >
                        <QuestionIcon16pxSvg className='fill-[#BDBDBD]' />
                     </DefaultTooltip>
                  </div>
                  <GrayIconButton onClick={onClose}>
                     <CloseIcon />
                  </GrayIconButton>
               </div>
            </DialogTitle>
            <DialogContent>
               <p className='pt-5'>{t('common.biometrics')}</p>
               <div className='w-full overflow-x-auto'>
                  <div className='flex gap-3 py-4'>
                     {Array(7)
                        .fill()
                        .map((_, index) => (
                           <div
                              className={`rounded border border-solid border-[#E5E7EB] p-1 relative 
                            `}
                              key={index}
                              onClick={() => handleDivClick(index)}
                           >
                              <img
                                 src={avatar_2}
                                 alt='biometric_image'
                                 className={`max-w-40 duration-300 ${
                                    selectedImageIndex === index ? 'transition-all border-[15px] border-[transparent]' : ''
                                 }`}
                              />
                              <div className='absolute rounded-md bg-[#F3F4F6] p-2 left-1/2 -bottom-3 transform -translate-x-1/2'>
                                 <p className='text-sm'>FACE</p>
                              </div>
                              <div className='absolute right-0 -bottom-1'>
                                 <CheckCircleIcon fontSize='large' style={{ color: '#16A34A' }} />
                              </div>
                           </div>
                        ))}
                  </div>
               </div>
               <div className='pt-5'>
                  <p>{t('common.please-browse-or-select-from-history-to-enroll')}</p>
               </div>
               <div className='flex mt-5 sm:justify-end gap-3 flex-wrap'>
                  <div>
                     <ControlpanelSuccessButton onClick={handleOpenEnrollFromHistory}>
                        <AddIcon />
                        <p>{t('button.enroll-from-history')}</p>
                     </ControlpanelSuccessButton>
                  </div>
                  <div className='flex'>
                     <div>
                        <ControlpanelPrimaryButton className='!gap-[5px] !rounded-r-none' onClick={handleIconClick}>
                           <UploadIcon14pxSvg />
                           <p>{selectedType}</p>
                        </ControlpanelPrimaryButton>
                        <input type='file' ref={imageInputRef} className='hidden' onChange={handleImageChange} accept='image/*' />
                     </div>
                     <div className='flex'>
                        <ControlpanelPrimaryButton
                           className='!py-0 max-h-[39px] !min-w-8 !rounded-l-none'
                           onClick={handleToggleSelectType}
                           ref={anchorRefSelectType}
                           id='select-biometric-type-button'
                           aria-controls={openSelectType ? 'select-biometric-type-menu' : undefined}
                           aria-expanded={openSelectType ? 'true' : undefined}
                           aria-haspopup='true'
                        >
                           <ArrowDropDownIcon />
                        </ControlpanelPrimaryButton>
                        <div>
                           <Popper
                              open={openSelectType}
                              anchorEl={anchorRefSelectType.current}
                              placement='bottom-start'
                              transition
                              disablePortal
                              className='z-10'
                              style={{ position: 'absolute', top: 0, left: 0 }}
                           >
                              {({ TransitionProps, placement }) => (
                                 <Grow
                                    {...TransitionProps}
                                    style={{
                                       transformOrigin: placement === 'bottom' ? 'left top' : 'left bottom',
                                    }}
                                 >
                                    <Paper style={{ borderRadius: '6px', width: 'min-content' }}>
                                       <ClickAwayListener onClickAway={handleCloseSelectType}>
                                          <CustomAdvancedMenuList
                                             id='select-biometric-type-menu'
                                             aria-labelledby='select-biometric-type-button'
                                          >
                                             <MenuItem onClick={() => handleSelectedItemClick(t('button.upload-face-image'))}>
                                                <p>{t('button.upload-face-image')}</p>
                                             </MenuItem>
                                             <MenuItem onClick={() => handleSelectedItemClick(t('button.updoad-fingerprint'))}>
                                                <p>{t('button.updoad-fingerprint')}</p>
                                             </MenuItem>
                                             <MenuItem onClick={() => handleSelectedItemClick(t('button.upload-iris-image'))}>
                                                <p>{t('button.upload-iris-image')}</p>
                                             </MenuItem>
                                          </CustomAdvancedMenuList>
                                       </ClickAwayListener>
                                    </Paper>
                                 </Grow>
                              )}
                           </Popper>
                        </div>
                     </div>
                  </div>
                  <ControlpanelErrorButton disabled={selectedImageIndex === -1}>
                     <CloseIcon />
                     <p>{t('button.delete')}</p>
                  </ControlpanelErrorButton>
               </div>
            </DialogContent>
            <DialogActions>
               <div className='flex gap-2 px-4 py-4'>
                  <ControlpanelSecondaryButton onClick={onClose}>
                     <CloseIcon />
                     <p>{t('button.close')}</p>
                  </ControlpanelSecondaryButton>
               </div>
            </DialogActions>
         </CustomDialog>

         {openEnrollFromHistory && (
            <EnrollFromHistoryDialog
               onClose={handleCloseEnrollFromHistory}
               firstName={firstName}
               lastName={lastName}
               userGroup={userGroup}
            />
         )}
      </div>
   );
};

export default BiometricDataDialog;
